// LoginPage — 로그인 + 2단계 인증(이메일 OTP / 인증앱 TOTP).
import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI, clearLoggedOutMark, notifySessionChange, sessionAPI, touchHint } from '../api/client';
import useIsMobile from '../hooks/useIsMobile';
import { COLORS } from '../utils/uiStyles';
import { syncFontFromUser } from '../utils/fontPrefs';

// 서버가 이 기기엔 로그인 유지를 안 걸었을 때(X-Remember-Applied: 0) 안내를 보여 주는 시간.
const REMEMBER_NOTICE_MS = 1500;
const REMEMBER_NOT_APPLIED = '이 기기에서는 로그인 유지가 적용되지 않습니다.';

export default function LoginPage() {
  const [form, setForm] = useState({ email: '', password: '' });
  // 🔴 "이 기기에서 로그인 상태 유지" — **모바일에서만** 보이고, 보일 때는 **기본 켬**이며
  //    30일 동안 안 쓰면 풀린다. 표시 여부는 서버가 정한다(GET /api/session/eligibility 의
  //    remember_available) — useIsMobile 로 정하지 않는다. 뷰포트로 정하면 데스크톱 모드로
  //    바꾼 폰·데스크톱 UA 를 보내는 iPad 에서 화면과 서버 판정이 어긋난다.
  //    2FA 단계까지는 서버 challenge 가 이 값을 나른다(verify-2fa 본문엔 넣지 않는다).
  const [rememberAvailable, setRememberAvailable] = useState(false);
  const [remember, setRemember] = useState(false);
  // 체크했는데 응답 헤더가 X-Remember-Applied: 0 이면 안내를 보인 뒤 잠깐 있다 이동한다.
  const [rememberNotice, setRememberNotice] = useState(false);
  const [twofa, setTwofa] = useState(null);   // null 또는 { stage, email, challenge, qr, secret, message }
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const noticeTimer = useRef(null);
  const navigate = useNavigate();
  const mobile = useIsMobile();   // 모바일(≤768px): 카드 전체폭·터치 타깃 확대(인증 흐름 무변경)
  const S = styles(mobile);

  // 이 기기에 '로그인 유지'를 줄 수 있는지 서버에 묻는다(인증·부작용 없는 GET).
  // 🔴 실패·미구현·목 어댑터의 거절은 전부 삼키고 체크박스를 숨긴다(remember=false 유지) —
  //    부가 질의 하나 때문에 로그인 화면이 막히면 안 된다. 반환이 약속이 아닐 수도 있어
  //    Promise.resolve 로 감싼다(client 모듈을 목킹한 테스트).
  useEffect(() => {
    let alive = true;
    const ask = (sessionAPI && typeof sessionAPI.eligibility === 'function')
      ? sessionAPI.eligibility() : null;
    Promise.resolve(ask).then((res) => {
      if (!alive) return;
      const available = res?.data?.remember_available === true;
      setRememberAvailable(available);
      setRemember(available);   // 보이면 기본 켬, 안 보이면 끈 상태로 보낸다
    }).catch(() => { /* 판정 불가 = 체크박스 숨김 */ });
    return () => { alive = false; };
  }, []);

  // 안내 타이머는 언마운트 시 정리한다(남으면 사라진 화면에서 이동이 돈다).
  useEffect(() => () => { if (noticeTimer.current) clearTimeout(noticeTimer.current); }, []);

  const apiError = (err, fallback) => {
    const detail = err.response?.data?.detail;
    if (detail && typeof detail === 'object') {
      return { code: detail.code, message: detail.message || fallback, description: detail.description };
    }
    return { message: detail || fallback };
  };

  const finish = (data) => {
    localStorage.setItem('token', data.access_token);
    localStorage.setItem('user', JSON.stringify(data.user));
    syncFontFromUser(data.user);   // 로그인 즉시 개인 폰트 적용
    // 로그아웃 표식을 지우고(다음 부팅의 resume 을 다시 허용) App 의 공유 부팅 상태를 깨운다
    // → SPA 재로드 없이 authenticated 로 전이해 /home 보호 라우트가 바로 열린다.
    // 🔴 client 모듈을 목킹한 기존 테스트에서도 죽지 않게 함수 존재를 확인한다.
    if (typeof clearLoggedOutMark === 'function') clearLoggedOutMark();
    if (typeof notifySessionChange === 'function') notifySessionChange();
    // SSO 등에서 ?next=로 돌아갈 경로 지정 시 그쪽으로(내부 경로만 허용 = 오픈 리다이렉트 방지)
    const next = new URLSearchParams(window.location.search).get('next');
    if (next && next.startsWith('/') && !next.startsWith('//')) window.location.replace(next);
    else navigate('/home');
  };

  // 서버는 본문 키를 늘리지 않고 헤더로만 알린다 — X-Remember-Applied: 1|0(M1b).
  //    헤더는 AxiosHeaders 일 수도, 평범한 객체일 수도 있다(목 응답).
  const rememberApplied = (res) => {
    const h = res?.headers;
    if (!h) return undefined;
    return typeof h.get === 'function' ? h.get('x-remember-applied') : h['x-remember-applied'];
  };

  // 체크했는데 서버가 이 기기엔 안 걸었으면(0) 안내를 보이고 REMEMBER_NOTICE_MS 뒤에 이동한다.
  // 그 사이 제출 버튼은 비활성이다(같은 자격증명으로 두 번 로그인하지 않게).
  const finishWithNotice = (res) => {
    if (remember && rememberApplied(res) === '0') {
      setRememberNotice(true);
      noticeTimer.current = setTimeout(() => {
        noticeTimer.current = null;
        finish(res.data);
      }, REMEMBER_NOTICE_MS);
      return;
    }
    finish(res.data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null); setLoading(true);
    try {
      // 🔴 touch 를 함께 싣는다 — iPadOS 13+ 는 데스크톱 UA 라 서버가 UA 만으로는 모바일로
      //    판정하지 못한다(backend/services/device_class.py). 목 client 엔 없을 수 있어 확인한다.
      const touch = (typeof touchHint === 'function' ? touchHint() : 0) === 1;
      const res = await authAPI.login({ ...form, remember, touch });
      if (res.data.require_2fa) { setTwofa(res.data); setCode(''); }
      else finishWithNotice(res);
    } catch (err) {
      setError(apiError(err, '로그인에 실패했습니다.'));
    } finally { setLoading(false); }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError(null); setLoading(true);
    try {
      // 🔴 verify-2fa 본문엔 remember·touch 를 넣지 않는다 — 서버가 challenge 로 나르고,
      //    2FA 확인 엔드포인트는 touch 를 스키마로 받지 않는다(backend/routers/auth.py).
      const res = await authAPI.verify2fa({ challenge: twofa.challenge, code });
      finishWithNotice(res);
    } catch (err) {
      setError(apiError(err, '인증에 실패했습니다.'));
    } finally { setLoading(false); }
  };

  const backToLogin = () => { setTwofa(null); setCode(''); setError(null); };

  // DEV 환경 표시: 운영 도메인(ai.ourbox.co.kr)이 아니면 개발환경 → 로그인 화면에 크게 'DEV' 배지(모바일 포함).
  const isDevEnv = (typeof window !== 'undefined') && window.location.hostname !== 'ai.ourbox.co.kr';

  // 로그인·2FA 확인 어느 쪽에서 와도 같은 안내를 쓴다(두 폼 모두에 렌더).
  const rememberNoticeBox = rememberNotice
    ? <p style={S.info} role="status">{REMEMBER_NOT_APPLIED}</p>
    : null;

  return (
    <div style={S.wrap}>
      <div style={S.card}>
        {isDevEnv && (
          <div style={{ textAlign: 'center', marginBottom: '12px' }}>
            <span style={{ display: 'inline-block', background: '#dc2626', color: '#fff', fontWeight: 800, letterSpacing: '2px', fontSize: mobile ? '22px' : '26px', padding: '4px 18px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(220,38,38,.4)' }}>DEV</span>
            <div style={{ marginTop: '6px', fontSize: '12px', color: '#b91c1c', fontWeight: 600 }}>개발 환경 (운영 아님)</div>
          </div>
        )}
        <h1 style={S.title}>아워박스 AI 게이트웨이</h1>

        {!twofa ? (
          <form onSubmit={handleSubmit} style={S.form}>
            <label style={S.label}>이메일</label>
            <input style={S.input} type="email" placeholder="name@ourbox.co.kr" autoComplete="email" spellCheck={false}
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            <label style={S.label}>비밀번호</label>
            <input style={S.input} type="password" placeholder="비밀번호" autoComplete="current-password"
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
            {/* 🔴 모바일에서만 보인다(표시 판정 = 서버 eligibility) · 기본 켬 · 30일 동안 안 쓰면
                풀린다. PC 에는 아예 렌더하지 않는다 — 켜도 서버가 무시하고 0 을 내린다(M1b). */}
            {rememberAvailable && (
              <label style={S.remember}>
                <input type="checkbox" style={S.checkbox} checked={remember}
                  onChange={(e) => setRemember(e.target.checked)} />
                <span>이 기기에서 로그인 상태 유지</span>
              </label>
            )}
            {error && (error.code === 'approval_pending' ? (
              <div style={S.info} role="status">
                <p style={S.infoTitle}>{error.message}</p>
                <p style={S.infoText}>{error.description || '승인 완료 후 로그인할 수 있습니다.'}</p>
                <p style={{ ...S.infoText, marginBottom: 0 }}>승인이 지연되면 관리자에게 문의해 주세요.</p>
              </div>
            ) : <p style={S.err} role="alert">{error.message}</p>)}
            {rememberNoticeBox}
            <button style={{ ...S.btn, ...((loading || rememberNotice) ? S.btnDisabled : {}) }} type="submit" disabled={loading || rememberNotice}>
              {loading ? '로그인 중...' : '로그인'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerify} style={S.form}>
            <p style={S.twofaTitle}>2단계 인증</p>
            <p style={S.twofaMsg}>{twofa.message}</p>
            {twofa.stage === 'app_enroll' && (
              <div style={S.qrBox}>
                {twofa.qr && <img src={twofa.qr} alt="2FA QR" style={S.qrImage} />}
                {twofa.secret && (
                  <p style={S.secretText}>
                    QR을 못 읽으면 키 직접 입력:<br /><code style={S.secretCode}>{twofa.secret}</code>
                  </p>
                )}
              </div>
            )}
            <input style={{ ...S.input, textAlign: 'center', letterSpacing: '6px', fontSize: '20px' }}
              type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="000000" autoFocus
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} required />
            {error && <p style={S.err} role="alert">{error.message}</p>}
            {rememberNoticeBox}
            <button style={{ ...S.btn, ...((loading || rememberNotice || code.length < 6) ? S.btnDisabled : {}) }} type="submit" disabled={loading || rememberNotice || code.length < 6}>
              {loading ? '확인 중...' : '인증하고 로그인'}
            </button>
            <p style={S.link}><button type="button" onClick={backToLogin} style={S.linkBtn}>← 다시 로그인</button></p>
          </form>
        )}

        {!twofa && (
          <>
            <p style={S.link}><Link to="/forgot-password">비밀번호를 잊으셨나요?</Link></p>
            <p style={S.link}>계정이 없으신가요? <Link to="/register">회원가입</Link></p>
          </>
        )}
      </div>
    </div>
  );
}

// 데스크톱(mobile=false)은 기존 360px 중앙 카드 그대로. 모바일은 전체폭·터치 타깃 확대·safe-area.
const styles = (mobile) => ({
  wrap: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'var(--gw-app-h, 100dvh)', background: '#f5f5f5', boxSizing: 'border-box',
    padding: mobile ? 'calc(env(safe-area-inset-top) + 16px) 16px calc(env(safe-area-inset-bottom) + 16px)' : 0 },
  card: { background: 'white', padding: mobile ? '28px 20px' : '40px', borderRadius: '12px', boxShadow: mobile ? 'none' : '0 2px 12px rgba(0,0,0,.1)',
    width: mobile ? '100%' : '360px', maxWidth: mobile ? '420px' : undefined, boxSizing: 'border-box' },
  title: { textAlign: 'center', marginBottom: '24px', fontSize: mobile ? '20px' : '22px', fontWeight: '700' },
  form: { display: 'flex', flexDirection: 'column' },
  label: { fontSize: '12px', color: '#4b5563', fontWeight: 700, marginBottom: '6px' },
  input: { width: '100%', padding: mobile ? '13px 14px' : '10px 12px', marginBottom: '12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: mobile ? '16px' : '14px', boxSizing: 'border-box', minHeight: mobile ? '46px' : '40px', outline: 'none' },
  btn: { width: '100%', padding: mobile ? '14px' : '12px', minHeight: mobile ? '48px' : '42px', background: COLORS.primary, color: 'white', border: 'none', borderRadius: '6px', fontSize: '15px', fontWeight: 700, cursor: 'pointer' },
  btnDisabled: { background: COLORS.border, color: COLORS.textMuted, cursor: 'not-allowed' },
  remember: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#374151', margin: '0 0 12px', cursor: 'pointer', userSelect: 'none' },
  checkbox: { width: mobile ? '20px' : '16px', height: mobile ? '20px' : '16px', margin: 0, accentColor: COLORS.primary, cursor: 'pointer' },
  err: { color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 12px', fontSize: '13px', margin: '0 0 12px', lineHeight: 1.5 },
  info: { color: '#374151', background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '12px', fontSize: '13px', margin: '0 0 12px', lineHeight: 1.5 },
  infoTitle: { color: '#334155', fontWeight: 700, margin: '0 0 4px' },
  infoText: { color: '#64748b', margin: '0 0 10px' },
  secondaryBtn: { width: '100%', minHeight: '36px', background: 'white', color: COLORS.info, border: `1px solid ${COLORS.primaryBorder}`, borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 700 },
  link: { textAlign: 'center', marginTop: '12px', fontSize: '13px' },
  linkBtn: { background: 'none', border: 'none', color: COLORS.info, cursor: 'pointer', fontSize: '13px' },
  twofaTitle: { textAlign: 'center', fontSize: '16px', fontWeight: '700', marginBottom: '6px' },
  twofaMsg: { textAlign: 'center', fontSize: '13px', color: '#6b7280', marginBottom: '16px', lineHeight: '1.5' },
  qrBox: { textAlign: 'center', marginBottom: '14px', padding: '12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '10px' },
  qrImage: { width: mobile ? '168px' : '180px', height: mobile ? '168px' : '180px' },
  secretText: { fontSize: '12px', color: '#6b7280', margin: '8px 0 0', lineHeight: 1.5 },
  secretCode: { fontSize: '12px', wordBreak: 'break-all' },
});
